/*
 * Procedural mesh builders for the 3D startup loader. Everything is built on
 * the CPU once, as plain arrays, and uploaded by renderer.ts.
 *
 * Conventions: the engine axis is +X (inlet at low x). A point at axial
 * station x, radius r and angle θ is (x, r·cosθ, r·sinθ), so θ = 0 is top.
 *
 * Every vertex carries `data = (u, cut)`:
 *   u   - sweep parameter 0..1 (tubes: distance along the path) used by the
 *         shader to "grow" pipes and harnesses into place;
 *   cut - 1 on section faces of cutaways, drawn as teal-lit caps.
 * Edges are generated explicitly per primitive (profile corners, blade
 * outlines, box edges) rather than by dihedral analysis: cheaper and cleaner.
 */

import { Mat4, Vec3, mat4, v3 } from './math';

export type P2 = [number, number];

export interface MeshData {
  pos: Float32Array;
  nrm: Float32Array;
  data: Float32Array;
  idx: Uint32Array;
  /** Line segments, 4 floats per vertex: x, y, z, u. */
  edges: Float32Array;
  tris: number;
}

export class MeshBuilder {
  p: number[] = [];
  n: number[] = [];
  d: number[] = [];
  i: number[] = [];
  e: number[] = [];

  get vertexCount() {
    return this.p.length / 3;
  }

  vert(p: Vec3, n: Vec3, u = 0, cut = 0) {
    this.p.push(p[0], p[1], p[2]);
    this.n.push(n[0], n[1], n[2]);
    this.d.push(u, cut);
    return this.p.length / 3 - 1;
  }

  tri(a: number, b: number, c: number) {
    this.i.push(a, b, c);
  }

  quad(a: number, b: number, c: number, d: number) {
    this.i.push(a, b, c, a, c, d);
  }

  edge(a: Vec3, b: Vec3, u = 0) {
    this.e.push(a[0], a[1], a[2], u, b[0], b[1], b[2], u);
  }

  polyline(pts: Vec3[], closed = false, u = 0) {
    for (let k = 0; k + 1 < pts.length; k++) this.edge(pts[k], pts[k + 1], u);
    if (closed && pts.length > 2) this.edge(pts[pts.length - 1], pts[0], u);
  }

  /** Appends another builder, optionally transformed by a rigid matrix. */
  append(o: MeshBuilder, m?: Mat4) {
    const base = this.vertexCount;
    for (let k = 0; k < o.p.length; k += 3) {
      const p: Vec3 = [o.p[k], o.p[k + 1], o.p[k + 2]];
      const n: Vec3 = [o.n[k], o.n[k + 1], o.n[k + 2]];
      const tp = m ? mat4.transformPoint(m, p) : p;
      const tn = m ? mat4.transformDir(m, n) : n;
      this.p.push(tp[0], tp[1], tp[2]);
      this.n.push(tn[0], tn[1], tn[2]);
    }
    for (let k = 0; k < o.d.length; k++) this.d.push(o.d[k]);
    for (let k = 0; k < o.i.length; k++) this.i.push(o.i[k] + base);
    for (let k = 0; k < o.e.length; k += 4) {
      const p: Vec3 = [o.e[k], o.e[k + 1], o.e[k + 2]];
      const tp = m ? mat4.transformPoint(m, p) : p;
      this.e.push(tp[0], tp[1], tp[2], o.e[k + 3]);
    }
    return this;
  }

  /** Recomputes smooth normals for vertices from `fromVert` on. */
  smoothNormals(fromVert = 0, fromIdx = 0) {
    const acc = new Float64Array((this.vertexCount - fromVert) * 3);
    for (let k = fromIdx; k < this.i.length; k += 3) {
      const a = this.i[k], b = this.i[k + 1], c = this.i[k + 2];
      const pa: Vec3 = [this.p[a * 3], this.p[a * 3 + 1], this.p[a * 3 + 2]];
      const pb: Vec3 = [this.p[b * 3], this.p[b * 3 + 1], this.p[b * 3 + 2]];
      const pc: Vec3 = [this.p[c * 3], this.p[c * 3 + 1], this.p[c * 3 + 2]];
      const fn = v3.cross(v3.sub(pb, pa), v3.sub(pc, pa));
      for (const v of [a, b, c]) {
        const o = (v - fromVert) * 3;
        acc[o] += fn[0];
        acc[o + 1] += fn[1];
        acc[o + 2] += fn[2];
      }
    }
    for (let v = fromVert; v < this.vertexCount; v++) {
      const o = (v - fromVert) * 3;
      const nn = v3.norm([acc[o], acc[o + 1], acc[o + 2]]);
      this.n[v * 3] = nn[0];
      this.n[v * 3 + 1] = nn[1];
      this.n[v * 3 + 2] = nn[2];
    }
    return this;
  }

  build(): MeshData {
    return {
      pos: new Float32Array(this.p),
      nrm: new Float32Array(this.n),
      data: new Float32Array(this.d),
      idx: new Uint32Array(this.i),
      edges: new Float32Array(this.e),
      tris: this.i.length / 3,
    };
  }
}

const TAU = Math.PI * 2;
const at = (x: number, r: number, th: number): Vec3 => [x, r * Math.cos(th), r * Math.sin(th)];

/* ── ear clipping (for cutaway section caps) ──────────────────────────── */

function area2(pts: P2[]) {
  let a = 0;
  for (let k = 0; k < pts.length; k++) {
    const [x0, y0] = pts[k];
    const [x1, y1] = pts[(k + 1) % pts.length];
    a += x0 * y1 - x1 * y0;
  }
  return a / 2;
}

function inTri(p: P2, a: P2, b: P2, c: P2) {
  const s = (p1: P2, p2: P2, p3: P2) => (p1[0] - p3[0]) * (p2[1] - p3[1]) - (p2[0] - p3[0]) * (p1[1] - p3[1]);
  const d1 = s(p, a, b), d2 = s(p, b, c), d3 = s(p, c, a);
  const neg = d1 < 0 || d2 < 0 || d3 < 0;
  const pos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(neg && pos);
}

/** Triangulates a simple polygon; returns index triples into `pts`. */
export function earClip(pts: P2[]): number[] {
  const ids = pts.map((_, k) => k);
  if (area2(pts) < 0) ids.reverse();
  const out: number[] = [];
  let guard = 0;
  while (ids.length > 3 && guard++ < 5000) {
    let clipped = false;
    for (let k = 0; k < ids.length; k++) {
      const ia = ids[(k + ids.length - 1) % ids.length], ib = ids[k], ic = ids[(k + 1) % ids.length];
      const a = pts[ia], b = pts[ib], c = pts[ic];
      const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
      if (cross <= 1e-12) continue;
      let inside = false;
      for (const j of ids) {
        if (j === ia || j === ib || j === ic) continue;
        if (inTri(pts[j], a, b, c)) { inside = true; break; }
      }
      if (inside) continue;
      out.push(ia, ib, ic);
      ids.splice(k, 1);
      clipped = true;
      break;
    }
    if (!clipped) break;
  }
  if (ids.length === 3) out.push(ids[0], ids[1], ids[2]);
  return out;
}

/* ── lathe: surfaces of revolution ────────────────────────────────────── */

export interface LatheOpts {
  /** Segments for a full turn (scaled for partial arcs). */
  seg?: number;
  a0?: number;
  a1?: number;
  /** Profile is a closed polygon (a solid ring section). */
  closed?: boolean;
  /** Corner angle (deg) above which the profile is creased and edged. */
  sharp?: number;
  /** Draw ring edges at creases. */
  edges?: boolean;
  /** Extra ring edges every N segments of the profile (panel lines). */
  u?: number;
}

export function lathe(profile: P2[], o: LatheOpts = {}, b = new MeshBuilder()) {
  const { seg = 72, a0 = 0, a1 = TAU, closed = false, sharp = 32, edges = true, u = 0 } = o;
  const arc = a1 - a0;
  const full = arc >= TAU - 1e-6;
  const steps = Math.max(3, Math.ceil((seg * arc) / TAU));
  const n = profile.length;
  const segCount = closed ? n : n - 1;
  const segN: P2[] = [];
  const segT: P2[] = [];
  for (let s = 0; s < segCount; s++) {
    const [x0, r0] = profile[s];
    const [x1, r1] = profile[(s + 1) % n];
    const l = Math.hypot(x1 - x0, r1 - r0) || 1;
    segT.push([(x1 - x0) / l, (r1 - r0) / l]);
    segN.push([-(r1 - r0) / l, (x1 - x0) / l]);
  }
  const cosLim = Math.cos((sharp * Math.PI) / 180);
  const isSharp = (j: number) => {
    if (!closed && (j === 0 || j === n - 1)) return true;
    const sin = (j - 1 + segCount) % segCount;
    const sout = j % segCount;
    const d = segT[sin][0] * segT[sout][0] + segT[sin][1] * segT[sout][1];
    return d < cosLim;
  };
  const avg = (a: P2, c: P2): P2 => {
    const l = Math.hypot(a[0] + c[0], a[1] + c[1]) || 1;
    return [(a[0] + c[0]) / l, (a[1] + c[1]) / l];
  };
  const n3 = (nn: P2, th: number): Vec3 => [nn[0], nn[1] * Math.cos(th), nn[1] * Math.sin(th)];

  for (let s = 0; s < segCount; s++) {
    const j = s;
    const k = (s + 1) % n;
    const prev = (s - 1 + segCount) % segCount;
    const next = (s + 1) % segCount;
    const nj = isSharp(j) ? segN[s] : avg(segN[prev], segN[s]);
    const nk = isSharp(k) ? segN[s] : avg(segN[s], segN[next]);
    const base = b.vertexCount;
    for (let i = 0; i <= steps; i++) {
      const th = a0 + (arc * i) / steps;
      b.vert(at(profile[j][0], profile[j][1], th), n3(nj, th), u);
      b.vert(at(profile[k][0], profile[k][1], th), n3(nk, th), u);
    }
    for (let i = 0; i < steps; i++) {
      const v = base + i * 2;
      b.quad(v, v + 1, v + 3, v + 2);
    }
  }

  if (edges) {
    for (let j = 0; j < n; j++) {
      const [x, r] = profile[j];
      if (r < 1e-4 || !isSharp(j)) continue;
      const ring: Vec3[] = [];
      for (let i = 0; i <= steps; i++) ring.push(at(x, r, a0 + (arc * i) / steps));
      b.polyline(ring, false, u);
    }
  }

  if (!full) {
    for (const th of [a0, a1]) {
      const pts = profile.map(([x, r]) => at(x, r, th));
      if (edges) b.polyline(pts, closed, u);
      if (closed) {
        const nrm: Vec3 = [0, -Math.sin(th), Math.cos(th)];
        const base = b.vertexCount;
        profile.forEach(([x, r]) => b.vert(at(x, r, th), nrm, u, 1));
        const tris = earClip(profile);
        for (let t = 0; t < tris.length; t += 3) b.tri(base + tris[t], base + tris[t + 1], base + tris[t + 2]);
      }
    }
  }
  return b;
}

/** Samples a cubic Bézier in the (x, r) plane - for smooth lathe profiles. */
export function bezier2(p0: P2, c0: P2, c1: P2, p1: P2, steps = 10): P2[] {
  const out: P2[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const m = 1 - t;
    out.push([
      m * m * m * p0[0] + 3 * m * m * t * c0[0] + 3 * m * t * t * c1[0] + t * t * t * p1[0],
      m * m * m * p0[1] + 3 * m * m * t * c0[1] + 3 * m * t * t * c1[1] + t * t * t * p1[1],
    ]);
  }
  return out;
}

/* ── blades ───────────────────────────────────────────────────────────── */

export interface BladeSpec {
  /** Axial station of the hub leading edge. */
  x: number;
  rHub: number;
  rTip: number;
  chordHub: number;
  chordTip: number;
  /** Stagger from the axial direction, radians (negative for vanes). */
  staggerHub: number;
  staggerTip: number;
  /** Max thickness as a fraction of chord. */
  thick?: number;
  camber?: number;
  /** Axial shift of the leading edge at the tip (sweep). */
  sweep?: number;
  /** Circumferential shift at the tip, as arc length (lean). */
  lean?: number;
  spanSeg?: number;
  chordSeg?: number;
}

/** One twisted, cambered blade wrapped onto its cylinders, centred on θ = 0. */
export function blade(s: BladeSpec, b = new MeshBuilder()) {
  const { thick = 0.06, camber = 0.04, sweep = 0, lean = 0, spanSeg = 6, chordSeg = 6 } = s;
  const base = b.vertexCount;
  const baseIdx = b.i.length;
  const loop: P2[] = [];
  for (let i = chordSeg; i >= 0; i--) loop.push([0.5 * (1 - Math.cos((Math.PI * i) / chordSeg)), 1]);
  for (let i = 1; i < chordSeg; i++) loop.push([0.5 * (1 - Math.cos((Math.PI * i) / chordSeg)), -1]);
  const L = loop.length;
  const leIdx = chordSeg;
  const rows: Vec3[][] = [];
  for (let j = 0; j <= spanSeg; j++) {
    const t = j / spanSeg;
    const r = s.rHub + (s.rTip - s.rHub) * t;
    const c = s.chordHub + (s.chordTip - s.chordHub) * t;
    const beta = s.staggerHub + (s.staggerTip - s.staggerHub) * t;
    const ax0 = s.x + sweep * t * t;
    const tan0 = lean * t * t;
    const cb = Math.cos(beta), sb = Math.sin(beta);
    const row: Vec3[] = [];
    for (const [xc, side] of loop) {
      const yt = 5 * thick * (0.2969 * Math.sqrt(xc) - 0.126 * xc - 0.3516 * xc * xc + 0.2843 * xc ** 3 - 0.1036 * xc ** 4);
      const yc = 4 * camber * xc * (1 - xc);
      const a = xc * c;
      const bb = (yc + side * yt) * c;
      const axial = ax0 + a * cb - bb * sb;
      const tang = tan0 + a * sb + bb * cb;
      const p = at(axial, r, tang / r);
      row.push(p);
      b.vert(p, [1, 0, 0]);
    }
    rows.push(row);
  }
  for (let j = 0; j < spanSeg; j++) {
    for (let i = 0; i < L; i++) {
      const a = base + j * L + i;
      const c = base + j * L + ((i + 1) % L);
      b.quad(a, c, c + L, a + L);
    }
  }
  // Tip cap.
  const tip = rows[spanSeg];
  const cen = v3.scale(tip.reduce((acc, p) => v3.add(acc, p), [0, 0, 0] as Vec3), 1 / L);
  const ci = b.vert(cen, [1, 0, 0]);
  for (let i = 0; i < L; i++) b.tri(base + spanSeg * L + i, base + spanSeg * L + ((i + 1) % L), ci);
  b.smoothNormals(base, baseIdx);
  // Outline: leading edge, trailing edge, tip and root sections.
  b.polyline(rows.map(r => r[leIdx]));
  b.polyline(rows.map(r => r[0]));
  b.polyline(tip, true);
  b.polyline(rows[0], true);
  return b;
}

/** `count` copies of a blade around the axis, merged. */
export function bladeRing(s: BladeSpec, count: number, phase = 0, b = new MeshBuilder()) {
  const one = blade(s);
  for (let k = 0; k < count; k++) b.append(one, mat4.rotationX(phase + (TAU * k) / count));
  return b;
}

/* ── simple solids ────────────────────────────────────────────────────── */

export function box(sx: number, sy: number, sz: number, b = new MeshBuilder(), m?: Mat4) {
  const one = new MeshBuilder();
  const hx = sx / 2, hy = sy / 2, hz = sz / 2;
  const faces: [Vec3, Vec3, Vec3][] = [
    [[1, 0, 0], [0, 1, 0], [0, 0, 1]], [[-1, 0, 0], [0, 0, 1], [0, 1, 0]],
    [[0, 1, 0], [0, 0, 1], [1, 0, 0]], [[0, -1, 0], [1, 0, 0], [0, 0, 1]],
    [[0, 0, 1], [1, 0, 0], [0, 1, 0]], [[0, 0, -1], [0, 1, 0], [1, 0, 0]],
  ];
  const h: Vec3 = [hx, hy, hz];
  for (const [nn, uu, vv] of faces) {
    const c = [nn[0] * hx, nn[1] * hy, nn[2] * hz];
    const du = [uu[0] * h[0], uu[1] * h[1], uu[2] * h[2]];
    const dv = [vv[0] * h[0], vv[1] * h[1], vv[2] * h[2]];
    const corner = (su: number, sv: number): Vec3 => [
      c[0] + su * du[0] + sv * dv[0], c[1] + su * du[1] + sv * dv[1], c[2] + su * du[2] + sv * dv[2],
    ];
    const q = [corner(-1, -1), corner(1, -1), corner(1, 1), corner(-1, 1)];
    const i0 = one.vert(q[0], nn), i1 = one.vert(q[1], nn), i2 = one.vert(q[2], nn), i3 = one.vert(q[3], nn);
    one.quad(i0, i1, i2, i3);
    one.polyline(q, true);
  }
  return b.append(one, m);
}

/** Solid cylinder along +X from x0 to x1 (end discs included). */
export function cylinderX(x0: number, x1: number, r: number, seg = 16, b = new MeshBuilder(), m?: Mat4) {
  const one = lathe([[x0, 0], [x0, r], [x1, r], [x1, 0]], { seg });
  return b.append(one, m);
}

/** Torus around the X axis at station x (ring radius R, tube radius r). */
export function torusX(x: number, R: number, r: number, o: { seg?: number; minor?: number; a0?: number; a1?: number } = {}, b = new MeshBuilder()) {
  const { seg = 72, minor = 8, a0 = 0, a1 = TAU } = o;
  const prof: P2[] = [];
  for (let k = 0; k < minor; k++) {
    const phi = (TAU * k) / minor;
    prof.push([x + r * Math.cos(phi), R + r * Math.sin(phi)]);
  }
  return lathe(prof, { seg, a0, a1, closed: true, sharp: 80, edges: false }, b);
}

/** Matrix placing a +X-aligned primitive at a point, pointing along `dir`. */
export function orient(pos: Vec3, dir: Vec3): Mat4 {
  const x = v3.norm(dir);
  const helper: Vec3 = Math.abs(x[1]) < 0.9 ? [0, 1, 0] : [0, 0, 1];
  const z = v3.norm(v3.cross(x, helper));
  const y = v3.cross(z, x);
  const m = mat4.identity();
  m[0] = x[0]; m[1] = x[1]; m[2] = x[2];
  m[4] = y[0]; m[5] = y[1]; m[6] = y[2];
  m[8] = z[0]; m[9] = z[1]; m[10] = z[2];
  m[12] = pos[0]; m[13] = pos[1]; m[14] = pos[2];
  return m;
}

/* ── tubes (fuel/oil/bleed lines, harnesses) ──────────────────────────── */

function catmull(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, t: number): Vec3 {
  const t2 = t * t, t3 = t2 * t;
  const f = (a: number, b: number, c: number, d: number) =>
    0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
  return [f(p0[0], p1[0], p2[0], p3[0]), f(p0[1], p1[1], p2[1], p3[1]), f(p0[2], p1[2], p2[2], p3[2])];
}

export interface TubeOpts {
  radial?: number;
  samples?: number;
  /** Clamp bands every N metres of path length. */
  clampEvery?: number;
}

/** A tube swept along a Catmull-Rom spline through `path`; u = 0..1 along it. */
export function tube(path: Vec3[], radius: number, o: TubeOpts = {}, b = new MeshBuilder()) {
  const { radial = 7, samples = 8, clampEvery = 0 } = o;
  const pts: Vec3[] = [];
  const ext = [path[0], ...path, path[path.length - 1]];
  for (let k = 1; k < ext.length - 2; k++) {
    for (let i = 0; i < samples; i++) pts.push(catmull(ext[k - 1], ext[k], ext[k + 1], ext[k + 2], i / samples));
  }
  pts.push(path[path.length - 1]);
  const lens = [0];
  for (let k = 1; k < pts.length; k++) lens.push(lens[k - 1] + v3.len(v3.sub(pts[k], pts[k - 1])));
  const total = lens[lens.length - 1] || 1;

  // Parallel-transport frames.
  const tans = pts.map((p, k) => v3.norm(v3.sub(pts[Math.min(k + 1, pts.length - 1)], pts[Math.max(k - 1, 0)])));
  let nrm = v3.norm(v3.cross(tans[0], Math.abs(tans[0][1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]));
  const frames: [Vec3, Vec3][] = [];
  for (let k = 0; k < pts.length; k++) {
    if (k > 0) {
      const bin = v3.cross(tans[k - 1], tans[k]);
      if (v3.len(bin) > 1e-6) {
        const axis = v3.norm(bin);
        const ang = Math.acos(Math.max(-1, Math.min(1, v3.dot(tans[k - 1], tans[k]))));
        // Rodrigues rotation of nrm about axis.
        const c = Math.cos(ang), s = Math.sin(ang);
        nrm = v3.add(v3.add(v3.scale(nrm, c), v3.scale(v3.cross(axis, nrm), s)), v3.scale(axis, v3.dot(axis, nrm) * (1 - c)));
      }
    }
    frames.push([nrm, v3.cross(tans[k], nrm)]);
  }

  const ring = (center: Vec3, [nn, bb]: [Vec3, Vec3], rad: number, u: number) => {
    const first = b.vertexCount;
    for (let i = 0; i <= radial; i++) {
      const a = (TAU * i) / radial;
      const dir = v3.add(v3.scale(nn, Math.cos(a)), v3.scale(bb, Math.sin(a)));
      b.vert(v3.add(center, v3.scale(dir, rad)), dir, u);
    }
    return first;
  };
  const bridge = (r0: number, r1: number) => {
    for (let i = 0; i < radial; i++) b.quad(r0 + i, r0 + i + 1, r1 + i + 1, r1 + i);
  };

  let prev = -1;
  for (let k = 0; k < pts.length; k++) {
    const cur = ring(pts[k], frames[k], radius, lens[k] / total);
    if (prev >= 0) bridge(prev, cur);
    prev = cur;
  }

  if (clampEvery > 0) {
    for (let s = clampEvery * 0.5; s < total; s += clampEvery) {
      let k = 1;
      while (k < lens.length - 1 && lens[k] < s) k++;
      const w = radius * 1.4;
      const c0 = v3.sub(pts[k], v3.scale(tans[k], w));
      const c1 = v3.add(pts[k], v3.scale(tans[k], w));
      const u = s / total;
      const r0 = ring(c0, frames[k], radius * 1.7, u);
      const r1 = ring(c1, frames[k], radius * 1.7, u);
      bridge(r0, r1);
    }
  }
  return b;
}

/** Points on a circle around the engine axis, for routing tubes along cases. */
export function arcPath(x0: number, x1: number, r0: number, r1: number, th0: number, th1: number, n = 6): Vec3[] {
  const out: Vec3[] = [];
  for (let k = 0; k <= n; k++) {
    const t = k / n;
    out.push(at(x0 + (x1 - x0) * t, r0 + (r1 - r0) * t, th0 + (th1 - th0) * t));
  }
  return out;
}

export const polar = at;
